import liff from '@line/liff'
import { useStore } from '@/stores/index'
import router from '@/router'
import { intoUser } from '@/api/index.js'
import { useGA } from '@/composables/useGA'

export const useLiff = () => {
	const store = useStore()
	const { pushGAEvent } = useGA()

	const config = {
		liffId: import.meta.env.VITE_LIFF_ID,
		clientId: import.meta.env.VITE_LIFF_CLIENT_ID,
		isInClient: import.meta.env.VITE_isInClient,
		baseUrl: import.meta.env.VITE_API_BASE
	}
	// 初始化 LIFF
	const initLiff = async () => {
		store.setLoading(true) // 開啟 Loading
		if (!config.liffId) {
			window.alert('LIFF ID error')
			return
		}

		// liff.init() 會改動 URL，在此之前先把原始 search 存到 sessionStorage
		const preInitSearch = window.location.search

		if (!sessionStorage.getItem('redirectParams')) {
			sessionStorage.setItem('redirectParams', encodeURIComponent(preInitSearch))
		}

		// 先初始化 LIFF
		try {
			await liff.init({ liffId: config.liffId })
			if (!liff.isInClient()) {
				// alert(`不在LINE 瀏覽器中`)
				checkDevice()
				return
			}
			// -------------------------
			// 檢查是否登入
			if (!liff.isLoggedIn()) {
				liff.login({ redirectUri: window.location.href })
				return
			}

			// 登入後若有 ?r= 且 decode 後非空，代表有路徑需要還原，跳一次
			const currentUrl = new URL(window.location.href)
			const currentParams = new URLSearchParams(currentUrl.search)
			if (currentParams.get('r') !== null) {
				const decodedPath = window.atob(currentParams.get('r'))
				if (decodedPath) {
					location.href = `https://liff.line.me/${config.liffId}${decodedPath}`
					return
				}
				// decoded 為空 → 不需要跳，繼續往下執行
			}

			// -------------------------
			// 已登入，繼續執行流程

			// 做頁面帶參數
			await checkUrl()

			// 確認是否在活動期間
			const eventTimeStatus = checkTime()
			store.setTimeStatus(eventTimeStatus.state)
			if (!eventTimeStatus.state) {
				store.showAlert('alertDanger', eventTimeStatus.text, {}, () => {
					closeLiff() //line oa導流？
				})
				return
			}

			const friendship = await liff.getFriendship()
			if (!friendship.friendFlag) {
				addFriend()
				return
			}
			// 取得liff個人訊息
			const profile = await liff.getProfile()
			await store.setprofile(profile)

			// 使用者進入網站API
			const intoUserData = {
				userid: store.userId,
				displayname: store.displayName
			}

			const response = await intoUser(intoUserData)
			if (response.status === '200') {
				pushGAEvent('GA進站')
			} else if (response.status === '400') {
				if (response.is_friend == false) {
					alert('❌ 請加入官方帳號好友')
					addFriend()
				} else {
					console.log('其他錯誤', response.message)
					addFriend()
				}
			} else {
				console.log('使用者進入網站 其他狀態', response.message)
			}

			// 根據 page 參數進行路由導向
			const targetPage = store.pageName
			const targetToken = store.token
			const targetShareUID = store.shareUID

			if (targetPage === 'rules') {
				await router.push('/rules')
			} else if (targetPage === 'redeem') {
				await router.push('/redeem')
			} else if (targetToken) {
				if (router.currentRoute.value.name !== 'result') {
					await router.push(`/result?token=${targetToken}`)
				}
			} else if (targetShareUID) {
				// console.log('代表有人分享給使用者他點進來')
			} else {

			}


		} catch (err) {
			alert(`❌ LIFF 初始化失敗: ${err}`)
			store.stopLoading()
		}
	}

	// 進到確認時間
	const checkTime = () => {
		const nowTime = new Date()

		const startTime = new Date('2026/06/09 00:00:00')
		// const endTime = new Date('2026/07/06 23:59:59')
		const endTime = new Date('2026/10/19 00:00:00')
		if (nowTime <= startTime) {
			return { state: false, text: '活動尚未開始' }
		} else if (nowTime >= endTime) {
			return {
				state: false,
				text: '《解鎖你的 Vespa Vibe》<br>活動已結束，感謝您的熱情參與!<br>抽獎公告資訊請再鎖定Vespa 台灣FB官方粉絲專頁'
			}
		} else {
			return { state: true, text: '活動期間內' }
		}
	}

	const checkUrl = async () => {
		store.setPageName(null)

		// 1. 目前 URL（liff.init() 處理後）
		const searchParams = new URLSearchParams(window.location.search)
		const hash = window.location.hash
		const hashQuery = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : ''
		const hashParams = new URLSearchParams(hashQuery)

		// 2. liff.init() 後若還有 liff.state，從裡面解碼參數
		const liffStateRaw = searchParams.get('liff.state')
		let liffStateParams = null
		if (liffStateRaw) {
			const decoded = decodeURIComponent(liffStateRaw)
			const qIdx = decoded.indexOf('?')
			liffStateParams = qIdx !== -1 ? new URLSearchParams(decoded.slice(qIdx + 1)) : null
		}

		// 3. liff.init() 之前存的原始 URL（最可靠的 fallback）
		const savedRaw = sessionStorage.getItem('redirectParams')
		let savedParams = null
		let savedLiffStateParams = null
		if (savedRaw) {
			const decoded = decodeURIComponent(savedRaw) // e.g. "?token=123456" or "?liff.state=..."
			savedParams = new URLSearchParams(decoded)
			const savedLiffState = savedParams.get('liff.state')
			if (savedLiffState) {
				const inner = decodeURIComponent(savedLiffState)
				const qIdx = inner.indexOf('?')
				savedLiffStateParams = qIdx !== -1 ? new URLSearchParams(inner.slice(qIdx + 1)) : null
			}
		}
		sessionStorage.removeItem('redirectParams') // 用完清掉，避免下次殘留

		const get = (key) =>
			searchParams.get(key) || hashParams.get(key) || liffStateParams?.get(key) || savedParams?.get(key) || savedLiffStateParams?.get(key) || null

		let pageTarget = get('page')
		let tokenTarget = get('tk') || get('token')
		let shareUID = get('share')

		if (pageTarget) store.setPageName(pageTarget)
		if (tokenTarget) store.setToken(tokenTarget)
		if (shareUID) store.setShareUID(shareUID)
	}

	// ======================== 🎯 分享功能 ========================

	/**
	 * 分享功能
	 */
	const shareTarget = async (videoUrl, imgUrl, uid) => {
		try {
			const res = await liff.shareTargetPicker(
				[
					{
						type: 'video',
						originalContentUrl: videoUrl,
						previewImageUrl: imgUrl
					},
					{
						type: 'flex',
						altText: '我專屬的 Vespa 音樂短片出爐🎵 你覺得像我的 Vibe 嗎？快來解鎖體驗還能抽好禮！',
						contents: {
							type: 'bubble',
							body: {
								type: 'box',
								layout: 'vertical',
								contents: [
									{ type: 'text', text: '換你解鎖專屬 Vespa Vibe！', weight: 'bold', size: 'md' },
									{
										type: 'text',
										text: `你的好友 ${store.displayName} 已解鎖自己 Vespa 音樂短片✨ \n \n你會是哪種 Vespa Vibe🎧？ \n點擊下方按鈕👇立即解鎖體驗分享結果還能抽 Vespa 質感好禮🎁`,
										wrap: true,
										size: 'sm'
									}
								]
							},
							footer: {
								type: 'box',
								layout: 'vertical',
								spacing: 'sm',
								contents: [
									{
										type: 'button',
										style: 'primary',
										color: '#60B5B8',
										action: { type: 'uri', label: '立即解鎖', uri: `https://line.me/R/app/1654922673-3O1K9hXa/` }
									}
								]
							}
						}
					}
				],
				{ isMultiple: true }
			)

			// 實測：真的送出分享才會回傳 { status: 'success' }；取消（按 X）則回傳 undefined
			if (res?.status !== 'success') {
				return { success: false, cancelled: true }
			}

			pushGAEvent('分享成功')
			return { success: true, cancelled: false }
		} catch (error) {
			console.error('❌ 分享過程發生錯誤:', error)
			return { success: false, cancelled: false, error: error.message }
		}
	}

	// 行動端/PC判斷
	const checkDevice = () => {
		const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone/i.test(navigator.userAgent)

		if (isMobile) {
			const url = new URL(window.location.href)
			const base = import.meta.env.BASE_URL.replace(/\/$/, '') // "/unlock-vespa-vibe" 或 ""
			const path = url.pathname.replace(base, '') || '/' // "/redeem" 或 "/"

			if (path === '/') {
				location.href = `https://line.me/R/app/${config.liffId}?r=${window.btoa(url.search)}`
			} else {
				location.href = `https://line.me/R/app/${config.liffId}?r=${window.btoa(`${path}${url.search}`)}`
			}
		} else {
			document.body.innerHTML =
				'<div style="display: flex; background-color: #fff; align-items: center; justify-content: center; width: 100vw;height: 100vh; font-family: sans-serif; color: #666; flex-direction: column;margin: auto;"><img style=" width: 60vw;; background-repeat: no-repeat; background-size: contain;" src="https://lineevent.s3.ap-northeast-1.amazonaws.com/Vespa_New180/images/common/PC.jpg" alt="PC蓋板照片"></div>'
		}
	}

	// 關閉 LIFF
	const closeLiff = () => {
		console.log('關閉 LIFF 視窗')
		liff.closeWindow()
	}

	const addFriend = () => {
		// 用當前完整 URL 當 redirectUri，加完好友直接回到原本頁面
		const redirectEncoded = encodeURIComponent(window.location.href)
		const notFriendUrl = `https://liff.horyuken.com/lineAuth/index.html?debug=true&redirectUri=${redirectEncoded}&clientId=${config.clientId}`
		window.location.href = notFriendUrl
	}

	return {
		initLiff,
		closeLiff,
		checkTime,
		shareTarget,
		addFriend
	}
}
